import React, { useState, useMemo, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useCart } from './CartContext'
import { useAuth } from '../../Context/AuthContext'
import productMap from './productMap'
import { formatZAR, stripHtml } from './utils'
import { initialForm } from './constants'
import { shippingFee, getDirectFee, getLockerFee, getShippingDetails } from './shipping'
import { validate } from './validation'
import LockerModal from './LockerModal'
import { PaymentSuccess, PaymentCancelled, PaymentFailed } from './PaymentStatusPages'

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787'

const Checkout = () => {
  const { cartItems, cartCount, clearCart } = useCart()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const paymentStatus = searchParams.get('payment')
  const orderRef = searchParams.get('ref')

  const [shippingMethod, setShippingMethod] = useState('')
  const [selectedLocker, setSelectedLocker] = useState(null)
  const [showLockerModal, setShowLockerModal] = useState(false)
  const [form, setForm] = useState(initialForm)
  const [formErrors, setFormErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState('')

  const [couponCode, setCouponCode] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState(null)
  const [couponError, setCouponError] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)

  const entries = useMemo(
    () => Object.entries(cartItems).filter(([, qty]) => qty > 0),
    [cartItems],
  )

  const subtotal = useMemo(
    () =>
      entries.reduce((sum, [id, qty]) => {
        const p = productMap[id]
        return sum + (p?.price ?? 0) * qty
      }, 0),
    [entries],
  )

  const totalWeight = useMemo(
    () =>
      entries.reduce((sum, [id, qty]) => {
        const p = productMap[id]
        return sum + (p?.weight ?? 1) * qty
      }, 0),
    [entries],
  )

  const total = subtotal + shippingFee(shippingMethod, totalWeight, subtotal)

  const discount = useMemo(() => {
    if (!appliedCoupon) return 0
    if (appliedCoupon.type === 'percent') {
      return Math.round(subtotal * (appliedCoupon.value / 100) * 100) / 100
    }
    return appliedCoupon.value
  }, [appliedCoupon, subtotal])

  const finalTotal = Math.max(0, total - discount)

  const handleRedeemCoupon = async () => {
    setCouponError('')
    const code = couponCode.trim().toUpperCase()
    if (!code) {
      setCouponError('Please enter a coupon code.')
      return
    }

    const token = localStorage.getItem('token')
    if (!token) {
      setCouponError('Please sign in to use a coupon.')
      return
    }

    setCouponLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/redeem-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ coupon: code }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to redeem coupon')
      setAppliedCoupon(data.coupon)
      setCouponError('')
    } catch (err) {
      setCouponError(err.message)
    } finally {
      setCouponLoading(false)
    }
  }

  useEffect(() => {
    if (paymentStatus === 'success') {
      clearCart()

      const details = sessionStorage.getItem('orderDetails')
      if (!details) return

      const parsed = JSON.parse(details)
      if (orderRef && parsed.orderReference !== orderRef) {
        sessionStorage.removeItem('orderDetails')
        return
      }

      const { orderReference, checkoutId, customerName, customerEmail, customerPhone, lineItems, totalAmount, totalWeight, shippingMethod, shippingDetails } = parsed

      const proceed = async () => {
        try {
          const verifyRes = await fetch(`${API_URL}/api/verify-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ checkoutId }),
          })
          const verifyData = await verifyRes.json()
          if (!verifyData.verified) {
            sessionStorage.removeItem('orderDetails')
            return
          }
        } catch {
          sessionStorage.removeItem('orderDetails')
          return
        }

        const authToken = localStorage.getItem('token')
        if (authToken) {
          fetch(`${API_URL}/api/orders`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ orderReference, totalAmount, lineItems, shippingMethod }),
          }).catch(() => {})
        }

        fetch(`${API_URL}/api/send-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderReference, checkoutId, customerName, customerEmail, customerPhone, lineItems, totalAmount, totalWeight, shippingMethod, shippingDetails }),
        }).catch(() => {})

        sessionStorage.removeItem('orderDetails')
      }

      proceed()
    }
  }, [paymentStatus, clearCart, orderRef])

  useEffect(() => {
    if (paymentStatus === 'cancelled' || paymentStatus === 'failed') {
      sessionStorage.removeItem('orderDetails')
    }
  }, [paymentStatus])

  useEffect(() => {
    if (user) {
      let addr = {}
      if (user.address) {
        try {
          addr = typeof user.address === 'string' ? JSON.parse(user.address) : user.address
        } catch {
          addr = {}
        }
      }
      setForm(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || '',
        address: prev.address || addr.street || '',
        city: prev.city || addr.city || '',
        province: prev.province || addr.province || '',
        postalCode: prev.postalCode || addr.postalCode || '',
      }))
    }
  }, [user])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setPaymentError('')

    sessionStorage.removeItem('orderDetails')

    const errors = validate(form, shippingMethod, selectedLocker)
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)

    const origin = window.location.origin
    const amountInCents = Math.round(finalTotal * 100)

    const lineItems = entries.map(([id, qty]) => {
      const p = productMap[id]
      return {
        displayName: stripHtml(p?.name) || id,
        quantity: qty,
        pricingDetails: { price: Math.round((p?.price ?? 0) * 100) },
        image_url: p?.image ? new URL(p.image, origin).href : '',
      }
    })

    const fee = shippingFee(shippingMethod, totalWeight, subtotal)
    if (fee > 0) {
      lineItems.push({
        displayName: shippingMethod === 'direct' ? 'Shipping fee' : 'Locker fee',
        quantity: 1,
        pricingDetails: { price: Math.round(fee * 100) },
      })
    }

    if (appliedCoupon && discount > 0) {
      lineItems.push({
        displayName: `Coupon (${appliedCoupon.code})`,
        quantity: 1,
        pricingDetails: { price: -Math.round(discount * 100) },
      })
    }

    const metadata = {
      customerName: stripHtml(form.name),
      customerEmail: stripHtml(form.email),
      customerPhone: stripHtml(form.phone),
      shippingMethod,
    }
    if (selectedLocker) {
      metadata.lockerId = stripHtml(selectedLocker.id)
      metadata.lockerName = stripHtml(selectedLocker.name)
      metadata.lockerAddress = `${stripHtml(selectedLocker.address)}, ${stripHtml(selectedLocker.city)}`
    }
    if (shippingMethod === 'direct') {
      metadata.shippingAddress = stripHtml(form.address)
      metadata.shippingCity = stripHtml(form.city)
      metadata.shippingProvince = stripHtml(form.province)
      metadata.shippingPostalCode = stripHtml(form.postalCode)
    }

    try {
      const checkoutHeaders = { 'Content-Type': 'application/json' }
      if (appliedCoupon) {
        const authToken = localStorage.getItem('token')
        if (authToken) checkoutHeaders.Authorization = `Bearer ${authToken}`
      }

      const res = await fetch(`${API_URL}/api/create-checkout`, {
        method: 'POST',
        headers: checkoutHeaders,
        body: JSON.stringify({
          amount: amountInCents,
          currency: 'ZAR',
          successUrl: `${origin}/Checkout?payment=success`,
          cancelUrl: `${origin}/Checkout?payment=cancelled`,
          failureUrl: `${origin}/Checkout?payment=failed`,
          metadata,
          lineItems,
          totalWeight,
          couponCode: appliedCoupon ? appliedCoupon.code : undefined,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setPaymentError(data.message || data.error || 'Payment could not be initiated. Please try again.')
        setSubmitting(false)
        return
      }

      sessionStorage.setItem('orderDetails', JSON.stringify({
        orderReference: data.orderReference,
        checkoutId: data.checkoutId,
        customerName: stripHtml(form.name),
        customerEmail: stripHtml(form.email),
        customerPhone: stripHtml(form.phone),
        lineItems,
        totalAmount: amountInCents,
        totalWeight,
        shippingMethod,
        shippingDetails: getShippingDetails(shippingMethod, selectedLocker, form),
      }))

      window.location.href = data.redirectUrl
    } catch {
      setPaymentError('Could not connect to the payment service. Please try again.')
      setSubmitting(false)
    }
  }

  if (paymentStatus === 'success') return <PaymentSuccess orderRef={orderRef} />
  if (paymentStatus === 'cancelled') return <PaymentCancelled orderRef={orderRef} />
  if (paymentStatus === 'failed') return <PaymentFailed orderRef={orderRef} />

  if (entries.length === 0) {
    return (
      <div className="min-h-screen bg-[#fde1ff] text-gray-900">
        <section className="border-b border-gray-200 bg-gradient-to-br from-violet-700 via-violet-800 to-indigo-900 px-4 py-14 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-violet-200">Tagers Shoppe</p>
            <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Checkout</h1>
          </div>
        </section>
        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
            <p className="text-lg font-medium text-gray-500">Your cart is empty</p>
            <Link
              to="/"
              className="mt-4 inline-block rounded-lg bg-violet-800 px-6 py-3 text-sm font-semibold text-white hover:bg-violet-500"
            >
              Continue Shopping
            </Link>
          </div>
        </section>
      </div>
    )
  }

  const shippingMethods = [
    {
      value: 'direct',
      title: 'Direct Shipping',
      description: 'Delivered straight to your door within 5-10 business days (Depending on order contents).',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
        </svg>
      ),
    },
    {
      value: 'collection',
      title: 'Collection',
      description: 'Collect from a PostNet near us at no extra charge. (Paddocks Milnerton, Canal Walk or Pinelands Howard Centre)',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
        </svg>
      ),
    },
    {
      value: 'locker',
      title: 'Courier Guy Locker',
      description: 'Deliver to a Courier Guy Parcel Locker near you.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-[#fde1ff] text-gray-900">
      <section className="border-b border-gray-200 bg-gradient-to-br from-violet-700 via-violet-800 to-indigo-900 px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-violet-200">Tagers Shoppe</p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Checkout</h1>
          {cartCount > 0 && (
            <p className="mt-2 text-violet-200">
              {cartCount} item{cartCount !== 1 ? 's' : ''} in your cart
            </p>
          )}
        </div>
      </section>

      <form onSubmit={handleSubmit}>
        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="space-y-8 lg:col-span-2">
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-bold text-gray-900">Shipping Method</h2>
                <div className="space-y-3">
                  {shippingMethods.map((method) => (
                    <label
                      key={method.value}
                      className={`flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition ${
                        shippingMethod === method.value
                          ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="shippingMethod"
                        value={method.value}
                        checked={shippingMethod === method.value}
                        onChange={(e) => {
                          setShippingMethod(e.target.value)
                          if (e.target.value !== 'locker') {
                            setSelectedLocker(null)
                          }
                        }}
                        className="mt-1 h-4 w-4 shrink-0 accent-violet-800"
                      />
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 shrink-0 text-violet-700">{method.icon}</span>
                        <div>
                          <p className="font-semibold text-gray-900">{method.title}</p>
                          <p className="mt-0.5 text-sm text-gray-500">{method.description}</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                {shippingMethod === 'locker' && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Courier Guy Locker
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowLockerModal(true)}
                      className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition ${
                        selectedLocker
                          ? 'border-violet-500 bg-white text-gray-900'
                          : 'border-gray-300 bg-white text-gray-400 hover:border-gray-400'
                      }`}
                    >
                      {selectedLocker
                        ? `${selectedLocker.name} — ${selectedLocker.address}, ${selectedLocker.city}`
                        : 'Select a locker location...'}
                    </button>
                    {formErrors.locker && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.locker}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-bold text-gray-900">Customer Information</h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={form.name}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-violet-500 ${
                        formErrors.name ? 'border-red-400' : 'border-gray-300'
                      }`}
                      placeholder="John Doe"
                    />
                    {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-violet-500 ${
                          formErrors.email ? 'border-red-400' : 'border-gray-300'
                        }`}
                        placeholder="john@example.com"
                      />
                      {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-violet-500 ${
                          formErrors.phone ? 'border-red-400' : 'border-gray-300'
                        }`}
                        placeholder="082 123 4567"
                      />
                      {formErrors.phone && <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>}
                    </div>
                  </div>

                  {shippingMethod === 'direct' && (
                    <>
                      <div>
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                          Street Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="address"
                          name="address"
                          type="text"
                          value={form.address}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-violet-500 ${
                            formErrors.address ? 'border-red-400' : 'border-gray-300'
                          }`}
                          placeholder="123 Main St"
                        />
                        {formErrors.address && <p className="mt-1 text-sm text-red-600">{formErrors.address}</p>}
                      </div>

                      <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="city"
                            name="city"
                            type="text"
                            value={form.city}
                            onChange={handleInputChange}
                            className={`mt-1 block w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-violet-500 ${
                              formErrors.city ? 'border-red-400' : 'border-gray-300'
                            }`}
                            placeholder="Cape Town"
                          />
                          {formErrors.city && <p className="mt-1 text-sm text-red-600">{formErrors.city}</p>}
                        </div>
                        <div>
                          <label htmlFor="province" className="block text-sm font-medium text-gray-700">
                            Province <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="province"
                            name="province"
                            type="text"
                            value={form.province}
                            onChange={handleInputChange}
                            className={`mt-1 block w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-violet-500 ${
                              formErrors.province ? 'border-red-400' : 'border-gray-300'
                            }`}
                            placeholder="Western Cape"
                          />
                          {formErrors.province && <p className="mt-1 text-sm text-red-600">{formErrors.province}</p>}
                        </div>
                        <div>
                          <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                            Postal Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="postalCode"
                            name="postalCode"
                            type="text"
                            value={form.postalCode}
                            onChange={handleInputChange}
                            className={`mt-1 block w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-violet-500 ${
                              formErrors.postalCode ? 'border-red-400' : 'border-gray-300'
                            }`}
                            placeholder="8001"
                          />
                          {formErrors.postalCode && <p className="mt-1 text-sm text-red-600">{formErrors.postalCode}</p>}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-bold text-gray-900">Order Summary</h2>

                <div className="max-h-64 space-y-3 overflow-y-auto">
                  {entries.map(([id, qty]) => {
                    const p = productMap[id]
                    const name = p?.name || id
                    const price = p?.price ?? 0
                    return (
                      <div key={id} className="flex items-center justify-between text-sm">
                        <div className="min-w-0 flex-1 truncate pr-2">
                          <span className="font-medium text-gray-900">{name}</span>
                          <span className="ml-1 text-gray-400">×{qty}</span>
                        </div>
                        <span className="shrink-0 font-medium text-gray-900">
                          {formatZAR(price * qty)}
                        </span>
                      </div>
                    )
                  })}
                </div>

                <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Subtotal</span>
                    <span>{formatZAR(subtotal)}</span>
                  </div>
                  {user && !appliedCoupon && (
                    <div className="pt-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Have a coupon?</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => { setCouponCode(e.target.value); setCouponError('') }}
                          placeholder="Coupon code"
                          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent uppercase"
                        />
                        <button
                          type="button"
                          onClick={handleRedeemCoupon}
                          disabled={couponLoading}
                          className="shrink-0 rounded-lg border border-violet-300 px-4 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 transition disabled:opacity-50"
                        >
                          {couponLoading ? '...' : 'Apply'}
                        </button>
                      </div>
                      {couponError && <p className="mt-1 text-xs text-red-600">{couponError}</p>}
                    </div>
                  )}
                  {appliedCoupon && (
                    <div className="flex items-center justify-between text-sm text-emerald-600">
                      <span className="flex items-center gap-1.5">
                        <span>Coupon ({appliedCoupon.code})</span>
                      </span>
                      <span>-{formatZAR(discount)}</span>
                    </div>
                  )}
                  {subtotal < 1000 && (
                    <p className="text-xs text-emerald-600 font-medium">
                      Spend {formatZAR(1000 - subtotal)} more for free shipping!
                    </p>
                  )}
                  {subtotal >= 1000 && (shippingMethod === 'direct' || shippingMethod === 'locker') && (
                    <p className="text-xs text-emerald-600 font-medium">
                      Free shipping on orders over R1,000!
                    </p>
                  )}
                  {shippingMethod === 'direct' && (
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>Shipping</span>
                      {shippingFee('direct', totalWeight, subtotal) > 0 ? (
                        <span>R{getDirectFee(totalWeight).toFixed(2)}</span>
                      ) : (
                        <span className="text-emerald-600">Free</span>
                      )}
                    </div>
                  )}
                  {shippingMethod === 'locker' && (
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>Locker Fee</span>
                      {shippingFee('locker', totalWeight, subtotal) > 0 ? (
                        <span>R{getLockerFee(totalWeight).toFixed(2)}</span>
                      ) : (
                        <span className="text-emerald-600">Free</span>
                      )}
                    </div>
                  )}
                  {shippingMethod === 'collection' && (
                    <div className="flex items-center justify-between text-sm text-emerald-600">
                      <span>Collection</span>
                      <span>Free</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-gray-200 pt-2 text-lg font-bold text-gray-900">
                    <span>Total</span>
                    <span>{formatZAR(finalTotal)}</span>
                  </div>
                </div>

                {paymentError && (
                  <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{paymentError}</p>
                )}

                <button
                  type="submit"
                  disabled={!shippingMethod || submitting}
                  className="mt-4 w-full rounded-lg bg-violet-800 px-6 py-3 text-base font-semibold text-white hover:bg-violet-900 transition disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Redirecting to payment...
                    </>
                  ) : (
                    'Place Order'
                  )}
                </button>

                <p className="mt-3 text-center text-xs text-gray-400">
                  By placing this order you agree to our Terms & Conditions.
                </p>
              </div>
            </div>
          </div>
        </section>
      </form>

      {showLockerModal && (
        <LockerModal
          onSelect={setSelectedLocker}
          onClose={() => setShowLockerModal(false)}
        />
      )}
    </div>
  )
}

export default Checkout
